import { useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { HUB_URL } from 'hooks/signalR';

import { locColor } from 'utils/debugger';

import {
  debugSignalRHubInitSelector,
  debugSignalRHubInvokeSelector,
  debugSignalRHubReceiveSelector,
} from './logger.selectors';

export function useLogSignalRInit(hub: HUB_URL) {
  const [{ color: colorInit, log: logInit }, setLogInit] = useAtom(
    debugSignalRHubInitSelector(hub),
  );

  function updateLogInit(log: boolean) {
    setLogInit((old) => ({
      ...old,
      log,
    }));
  }

  function updateColorInit(color: string) {
    setLogInit((old) => ({
      ...old,
      color,
    }));
  }

  return {
    colorInit,
    logInit,
    updateColorInit,
    updateLogInit,
  } as const;
}

export function useLogSignalRInvoke(hub: HUB_URL) {
  const [{ color: colorInvoke, log: logInvoke }, setLogInvoke] = useAtom(
    debugSignalRHubInvokeSelector(hub),
  );

  function updateLogInvoke(log: boolean) {
    setLogInvoke((old) => ({
      ...old,
      log,
    }));
  }

  function updateColorInvoke(color: string) {
    setLogInvoke((old) => ({
      ...old,
      color,
    }));
  }

  return {
    colorInvoke,
    logInvoke,
    updateColorInvoke,
    updateLogInvoke,
  } as const;
}

export function useLogSignalRReceive(hub: HUB_URL) {
  const [{ color: colorReceive, log: logReceive }, setLogReceive] = useAtom(
    debugSignalRHubReceiveSelector(hub),
  );

  function updateLogReceive(log: boolean) {
    setLogReceive((old) => ({
      ...old,
      log,
    }));
  }

  function updateColorReceive(color: string) {
    setLogReceive((old) => ({
      ...old,
      color,
    }));
  }

  return {
    colorReceive,
    logReceive,
    updateColorReceive,
    updateLogReceive,
  } as const;
}

/**
 * Returns a callback to read the state without subscribing to it.
 */
export function useLogSignalR(hub: HUB_URL) {
  const getLogSignalRHubInitJotai = useAtomCallback<any, any>(
    useCallback((get) => {
      return get(debugSignalRHubInitSelector(hub));
    }, []),
  );
  const getLogSignalRHubReceiveJotai = useAtomCallback<any, any>(
    useCallback((get) => {
      return get(debugSignalRHubReceiveSelector(hub));
    }, []),
  );
  const getLogSignalRHubInvokeJotai = useAtomCallback<any, any>(
    useCallback((get) => {
      return get(debugSignalRHubInvokeSelector(hub));
    }, []),
  );

  function logSignalRInit(text: string, ...etc: unknown[]) {
    const debugInit = getLogSignalRHubInitJotai(hub);

    if (!debugInit?.log) return;
    locColor(debugInit.color, text, ...etc);
  }

  function logSignalRInvoke(text: string, ...etc: unknown[]) {
    const debugInit = getLogSignalRHubInvokeJotai(hub);
    if (!debugInit?.log) return;
    locColor(debugInit.color, text, ...etc);
  }

  function logSignalRReceive(text: string, ...etc: unknown[]) {
    const debugInit = getLogSignalRHubReceiveJotai(hub);
    if (!debugInit?.log) return;
    locColor(debugInit.color, text, ...etc);
  }

  return { logSignalRReceive, logSignalRInit, logSignalRInvoke };
}
